import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@shared-ui/sheet";
import { Skeleton } from "@shared-ui/skeleton";
import { Textarea } from "@shared-ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, BanknoteArrowUp, ReceiptText } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { apiErrorMessage } from "@/lib/api-error";
import { formatRupees } from "@/lib/format";
import { useDebounce } from "@/lib/use-debounce";
import {
	ResolutionProgress,
	ResolveDisputePanel,
	usePermittedOutcomes,
} from "../components/dispute-resolution";
import { ReconcileConfirmDialog } from "../components/reconcile-confirm-dialog";
import {
	type AdminPaymentDispute,
	type AdminPaymentRecord,
	createPaymentDispute,
	getAdminPayment,
	getRefundReconcileStatus,
	listAdminPayments,
	reconcilePayouts,
	reconcileRefunds,
	releaseAdminPayment,
	resyncPayoutAccount,
} from "../services/payments-api.service";

function paymentTone(status: AdminPaymentRecord["transaction"]["status"]) {
	switch (status) {
		case "held":
			return "warning";
		case "released":
			return "success";
		case "refunded":
		case "partially_refunded":
			return "info";
		case "failed":
			return "danger";
		default:
			return "default";
	}
}

/**
 * Whether a row is a homeowner funding charge still marked `held`: the
 * backend's `isFundingCharge` test (a provider order id) narrowed to `held`.
 * The row `hireContractor` writes is `held` too, but it has no provider
 * order: it records a payout owed to a contractor, not money a homeowner
 * paid in.
 *
 * A funding charge stays `held` even after part of that escrow has already
 * been paid out to a contractor: `authorizeAssignmentPayment` spends escrow
 * without touching the funding row's status. So this flag, and the tiles
 * built from it, count rows still marked `held` among the ones listed --
 * they are not the escrow balance the backend derives elsewhere
 * (`getProjectPayments`: `totalFunded - totalReleased - refunded`).
 */
function isHeldFundingCharge(payment: AdminPaymentRecord): boolean {
	return (
		Boolean(payment.transaction.providerOrderId) &&
		payment.transaction.status === "held"
	);
}

/**
 * What an escrow row is, for the actions this page offers on it. The backend
 * refuses an admin release and the opening of a dispute on every row with no
 * milestone, and a row with no dispute has none to resolve, so only a
 * `milestone` row gets the release and dispute controls.
 */
type PaymentKind =
	| "milestone"
	| "top_up"
	| "contractor_assignment"
	| "project_level";

function paymentKindOf(payment: AdminPaymentRecord): PaymentKind {
	if (payment.transaction.milestoneId) return "milestone";
	switch (payment.transaction.metadata?.type) {
		case "top_up":
			return "top_up";
		case "contractor_assignment":
			return "contractor_assignment";
		default:
			return "project_level";
	}
}

/** How a row with no milestone is named, in the table and the detail sheet. */
const MILESTONE_LESS_LABELS: Record<
	Exclude<PaymentKind, "milestone">,
	string
> = {
	top_up: "Escrow top-up",
	contractor_assignment: "Contractor hold",
	project_level: "Project-level transaction",
};

/**
 * The Status filter's value for "duplicate charges only". Not an escrow status:
 * it maps to the list's `duplicateCharge` filter instead.
 */
const DUPLICATE_CHARGE_FILTER = "duplicate_charge";

/**
 * What finance ops does with a held duplicate charge. The backend refuses to
 * release one (409) and accepts only a full refund when its dispute is
 * resolved, so this names that path instead of offering Release.
 */
const DUPLICATE_CHARGE_GUIDANCE =
	"This payment is a duplicate charge: another payment already funded this milestone. It is never released to the designer. Refund it: open a dispute on it below, then resolve that dispute with Full refund.";

/** The same guidance once a dispute on the duplicate is open, or under way. */
function duplicateChargeGuidance(
	dispute: AdminPaymentDispute | null,
	canRefund: boolean,
): string {
	if (dispute?.status === "resolving") {
		return "This payment is a duplicate charge being refunded. Its progress is shown below.";
	}
	if (!canRefund) {
		return "This payment is a duplicate charge: another payment already funded this milestone. It is never released to the designer. Only an operator who can issue a full refund can resolve it.";
	}
	if (dispute?.status === "open" || dispute?.status === "under_review") {
		return "This payment is a duplicate charge: another payment already funded this milestone. It is never released to the designer. Resolve its dispute below with Full refund.";
	}
	return DUPLICATE_CHARGE_GUIDANCE;
}

/**
 * The list request for the Status filter's value and the search text. The
 * duplicate-charge choice is not an escrow status, so it is sent as the
 * `duplicateCharge` filter and no status.
 */
export function paymentListFilters(
	status: string,
	query: string,
): Record<string, string | undefined> {
	const duplicatesOnly = status === DUPLICATE_CHARGE_FILTER;
	return {
		status: status === "all" || duplicatesOnly ? undefined : status,
		duplicateCharge: duplicatesOnly ? "true" : undefined,
		query: query || undefined,
	};
}

/** The badge that marks a duplicate charge in the table and the detail sheet. */
function DuplicateChargeBadge() {
	return <Badge variant="danger">Duplicate charge</Badge>;
}

const NO_MILESTONE_ACTIONS =
	"This payment belongs to no milestone, so it cannot be released or disputed here.";

/**
 * What a row with no milestone is, given its kind AND its status: a top-up is
 * `pending` until its payment is confirmed, `held` once it is, and `failed`
 * when `supersedeStrandedSession` gives up on its order; a contractor hold is
 * `held` from the hire and `released` once `authorizeAssignmentPayment` pays
 * it. Any other pair gets only the line every such row shares.
 */
function milestoneLessExplanation(
	kind: Exclude<PaymentKind, "milestone">,
	status: AdminPaymentRecord["transaction"]["status"],
): string {
	if (kind === "top_up") {
		switch (status) {
			case "pending":
				return "A top-up the homeowner started. No payment for it has been recorded, so it adds nothing to this project's escrow. If the payment provider confirms a payment for it, it becomes held and is added to the escrow. It has no payee and no milestone, so there is nothing to release or dispute.";
			case "held":
				return "Money the homeowner added to this project's escrow. It has no payee and no milestone, so there is nothing to release or dispute. A contractor hired with it is paid from their own hold.";
			case "failed":
				return "A top-up set aside when the payment provider changed, before any payment for it was recorded. It adds nothing to this project's escrow, and a payment confirmed for it afterwards is not added either. It has no payee and no milestone, so there is nothing to release or dispute.";
		}
	}
	if (kind === "contractor_assignment") {
		switch (status) {
			case "held":
				return "A contractor's quote, held for them when the homeowner hired them. It is paid out only when the homeowner or designer authorizes payment for that assignment once it is completed; it cannot be released or disputed here. While the contractor is removed from the project, or if the designer has since hired them, nothing is paid from it.";
			case "released":
				return "A contractor's quote, paid out to them when the homeowner or designer authorized payment for the completed assignment. There is nothing left to release, and it cannot be disputed here.";
		}
	}
	return NO_MILESTONE_ACTIONS;
}

function disputeTone(status: AdminPaymentDispute["status"]) {
	switch (status) {
		case "open":
		case "under_review":
		case "resolving":
			return "warning";
		case "resolved_release":
			return "success";
		case "resolved_refund":
		case "resolved_partial":
			return "info";
		default:
			return "default";
	}
}

function rowSubtitle(payment: AdminPaymentRecord): string {
	const kind = paymentKindOf(payment);
	if (kind === "milestone") {
		return payment.milestone.title || "Milestone payment";
	}
	return MILESTONE_LESS_LABELS[kind];
}

export function PaymentsDashboardPage() {
	const queryClient = useQueryClient();
	const [status, setStatus] = useState("all");
	const [query, setQuery] = useState("");
	const debouncedQuery = useDebounce(query, 250);
	const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
		null,
	);
	const [releaseReason, setReleaseReason] = useState("");
	const [disputeReason, setDisputeReason] =
		useState<AdminPaymentDispute["reason"]>("incomplete_work");
	const [disputeDetails, setDisputeDetails] = useState("");
	const permittedOutcomes = usePermittedOutcomes();

	const paymentsQuery = useQuery({
		queryKey: ["admin-payments", status, debouncedQuery],
		queryFn: () =>
			listAdminPayments(paymentListFilters(status, debouncedQuery)),
		placeholderData: (previous) => previous,
	});

	const detailQuery = useQuery({
		queryKey: ["admin-payment-detail", selectedPaymentId],
		queryFn: () => getAdminPayment(selectedPaymentId as string),
		enabled: !!selectedPaymentId,
	});

	const detailKind = detailQuery.data ? paymentKindOf(detailQuery.data) : null;
	// A duplicate charge is refunded in full and never paid out: the backend
	// refuses any other outcome for one, so the panel offers only that.
	const resolvableOutcomes = detailQuery.data?.duplicateCharge
		? permittedOutcomes.filter((outcome) => outcome === "full_refund")
		: permittedOutcomes;
	const detailDispute = detailQuery.data?.latestDispute ?? null;

	const invalidatePayments = async () => {
		await queryClient.invalidateQueries({ queryKey: ["admin-payments"] });
		await queryClient.invalidateQueries({
			queryKey: ["admin-payment-detail", selectedPaymentId],
		});
	};

	const releaseMutation = useMutation({
		mutationFn: (input: { paymentId: string; reason: string }) =>
			releaseAdminPayment(input.paymentId, input.reason),
		onSuccess: async () => {
			await invalidatePayments();
			toast.success("Payment released");
			setReleaseReason("");
		},
	});

	const resyncMutation = useMutation({
		mutationFn: (accountId: string) => resyncPayoutAccount(accountId),
		onSuccess: async () => {
			toast.success("Payout account re-synced");
			await invalidatePayments();
		},
		onError: (error: unknown) => {
			toast.error(
				apiErrorMessage(error, "Payout account re-sync failed. Try again."),
			);
		},
	});

	const disputeMutation = useMutation({
		mutationFn: (input: {
			paymentId: string;
			reason: AdminPaymentDispute["reason"];
			details: string;
		}) =>
			createPaymentDispute(input.paymentId, {
				reason: input.reason,
				details: input.details,
			}),
		onSuccess: async () => {
			await invalidatePayments();
			toast.success("Dispute saved");
			setDisputeDetails("");
		},
	});

	const metrics = useMemo(() => {
		const payments = paymentsQuery.data ?? [];
		const heldFunding = payments.filter(isHeldFundingCharge);
		return {
			held: heldFunding.length,
			disputed: payments.filter((payment) => payment.latestDispute).length,
			valueHeld: heldFunding.reduce(
				(sum, payment) => sum + payment.transaction.amount,
				0,
			),
		};
	}, [paymentsQuery.data]);

	const reconcileStatusQuery = useQuery({
		queryKey: ["admin-refund-reconcile-status"],
		queryFn: getRefundReconcileStatus,
	});

	const reconcileMutation = useMutation({
		mutationFn: () => reconcileRefunds(),
		onSuccess: async (result) => {
			const stillPending =
				result.scanned - result.resolved - result.failed - result.flagged;
			toast.success(
				`Reconciled ${result.resolved}. ${result.failed} failed at provider. ${result.flagged} flagged. ${stillPending} still pending.`,
			);
			await invalidatePayments();
			await queryClient.invalidateQueries({
				queryKey: ["admin-refund-reconcile-status"],
			});
		},
		onError: (error: unknown) => {
			// A reconcile that is rate-limited answers 429 with the cooldown in its
			// message. "Check logs" sent the operator to a server they cannot read;
			// the server's own sentence says what to do ("Retry in 25s").
			toast.error(
				apiErrorMessage(error, "Refund reconciliation failed. Try again."),
			);
		},
	});

	const reconcilePayoutsMutation = useMutation({
		mutationFn: () => reconcilePayouts(),
		onSuccess: async (result) => {
			toast.success(
				`Checked ${result.scanned} held row(s) and ${result.reversalChecked} recent release(s). Recorded ${result.released} release(s). ${result.reversed} reversal(s). ${result.failed} failed at provider. ${result.flagged} flagged.`,
			);
			await invalidatePayments();
		},
		onError: (error: unknown) => {
			// Same as the refunds sweep: surface the server's reason, not "check
			// logs". A 429 names the cooldown.
			toast.error(
				apiErrorMessage(error, "Payout reconciliation failed. Try again."),
			);
		},
	});

	const pendingRefunds = reconcileStatusQuery.data?.pending ?? 0;
	const failedRefunds = reconcileStatusQuery.data?.failed ?? 0;
	/** Which reconcile trigger the dialog is confirming, or none. */
	const [reconcileConfirm, setReconcileConfirm] = useState<
		"refunds" | "payouts" | null
	>(null);

	return (
		<div className="grid gap-5">
			<div className="grid gap-2">
				<p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
					Payments operations
				</p>
				<h2 className="text-2xl font-semibold tracking-tight">Escrow desk</h2>
				<p className="text-sm text-muted-foreground">
					Search funding cases, inspect payout blockers, and resolve release or
					refund outcomes from one queue.
				</p>
			</div>
			{pendingRefunds > 0 ? (
				<Card className="border-amber-200 bg-amber-50/60 shadow-none">
					<CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
						<div className="grid gap-1">
							<p className="text-sm font-medium text-amber-900">
								{pendingRefunds} refund{pendingRefunds === 1 ? "" : "s"} pending
								reconciliation
							</p>
							<p className="text-xs text-amber-800/80">
								Dispute resolutions whose refund the payment provider reported
								pending. Reconciling asks the provider about each and finishes
								those it reports processed for the amount recorded.
							</p>
						</div>
						<Button
							variant="outline"
							size="sm"
							disabled={reconcileMutation.isPending}
							onClick={() => setReconcileConfirm("refunds")}
						>
							{reconcileMutation.isPending
								? "Reconciling…"
								: "Reconcile pending refunds"}
						</Button>
					</CardContent>
				</Card>
			) : null}
			{failedRefunds > 0 ? (
				<Card className="border-destructive/30 bg-destructive/5 shadow-none">
					<CardContent className="p-5">
						<p className="text-sm font-medium text-destructive">
							{failedRefunds} refund{failedRefunds === 1 ? "" : "s"} recorded
							failed reconciling
						</p>
						<p className="text-xs text-muted-foreground">
							The provider reported these failed, or reported an amount that
							does not match the row. Reconciliation will not retry them;
							support has to check each against Cashfree and record what
							actually happened.
						</p>
					</CardContent>
				</Card>
			) : null}
			<Card className="shadow-none">
				<CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
					<div className="grid gap-1">
						<p className="text-sm font-medium">
							Payouts the provider has not confirmed
						</p>
						<p className="text-xs text-muted-foreground">
							A release records an escrow row released only when the payment
							provider reports the transfer paid. This asks the provider about
							the rows a release attempted, and records the ones it has since
							paid.
						</p>
					</div>
					<Button
						variant="outline"
						size="sm"
						disabled={reconcilePayoutsMutation.isPending}
						onClick={() => setReconcileConfirm("payouts")}
					>
						{reconcilePayoutsMutation.isPending
							? "Reconciling…"
							: "Reconcile pending payouts"}
					</Button>
				</CardContent>
			</Card>
			<div className="grid gap-3 md:grid-cols-3">
				{[
					{
						label: "Held funding charges",
						note: "In the rows listed below",
						value: metrics.held,
						icon: ReceiptText,
					},
					{
						label: "Payments with disputes",
						note: null,
						value: metrics.disputed,
						icon: AlertTriangle,
					},
					{
						label: "Held funding value",
						note: "In the rows listed below",
						value: formatRupees(metrics.valueHeld),
						icon: BanknoteArrowUp,
					},
				].map((metric) => {
					const Icon = metric.icon;
					return (
						<Card key={metric.label} className="border-slate-200 shadow-none">
							<CardContent className="flex items-center justify-between p-5">
								<div>
									<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
										{metric.label}
									</p>
									<p className="mt-2 text-2xl font-semibold">{metric.value}</p>
									{metric.note ? (
										<p className="mt-1 text-xs text-muted-foreground">
											{metric.note}
										</p>
									) : null}
								</div>
								<div className="flex size-11 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
									<Icon className="size-5" />
								</div>
							</CardContent>
						</Card>
					);
				})}
			</div>
			<Card className="border-slate-200 shadow-none">
				<CardHeader>
					<CardTitle>Queue</CardTitle>
					<CardDescription>
						Sticky filters and side inspection keep list context intact while
						you act.
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4">
					<div className="grid gap-3 rounded-2xl border bg-slate-50/80 p-3 md:grid-cols-[minmax(0,1fr)_220px]">
						<Input
							placeholder="Search by project, provider order, phone, or transaction id"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
						/>
						<Select value={status} onValueChange={setStatus}>
							<SelectTrigger>
								<SelectValue placeholder="Filter status" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectItem value="all">All statuses</SelectItem>
									<SelectItem value="held">Held</SelectItem>
									<SelectItem value="released">Released</SelectItem>
									<SelectItem value="refunded">Refunded</SelectItem>
									<SelectItem value="partially_refunded">
										Partially refunded
									</SelectItem>
									<SelectItem value="failed">Failed</SelectItem>
									<SelectItem value={DUPLICATE_CHARGE_FILTER}>
										Duplicate charges
									</SelectItem>
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>

					{paymentsQuery.isLoading ? (
						<Skeleton className="h-72 w-full rounded-2xl" />
					) : (
						<div className="overflow-x-auto rounded-2xl border">
							<table className="w-full text-left text-sm">
								<thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-muted-foreground">
									<tr>
										<th className="px-4 py-3">Project</th>
										<th className="px-4 py-3">Amount</th>
										<th className="px-4 py-3">Status</th>
										<th className="px-4 py-3">Dispute</th>
										<th className="px-4 py-3">Payout</th>
									</tr>
								</thead>
								<tbody>
									{paymentsQuery.data?.map((payment) => (
										<tr
											key={payment.transaction.id}
											className="cursor-pointer border-t hover:bg-slate-50"
											onClick={() =>
												setSelectedPaymentId(payment.transaction.id)
											}
										>
											<td className="px-4 py-3">
												<div className="grid gap-1">
													<span className="font-medium">
														{payment.project.title}
													</span>
													<span className="text-xs text-muted-foreground">
														{rowSubtitle(payment)}
													</span>
												</div>
											</td>
											<td className="px-4 py-3 font-medium">
												{formatRupees(payment.transaction.amount)}
											</td>
											<td className="px-4 py-3">
												<div className="flex flex-wrap gap-1">
													<Badge
														variant={paymentTone(payment.transaction.status)}
													>
														{payment.transaction.status}
													</Badge>
													{payment.duplicateCharge ? (
														<DuplicateChargeBadge />
													) : null}
												</div>
											</td>
											<td className="px-4 py-3">
												{payment.latestDispute ? (
													<Badge
														variant={disputeTone(payment.latestDispute.status)}
													>
														{payment.latestDispute.status}
													</Badge>
												) : (
													<span className="text-muted-foreground">None</span>
												)}
											</td>
											<td className="px-4 py-3">
												{payment.payout.payoutReady ? (
													<Badge variant="success">Ready</Badge>
												) : (
													<Badge variant="warning">Blocked</Badge>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			<Sheet
				open={!!selectedPaymentId}
				onOpenChange={(open) => !open && setSelectedPaymentId(null)}
			>
				<SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
					{detailQuery.isLoading ? (
						<Skeleton className="h-full w-full rounded-2xl" />
					) : detailQuery.data ? (
						<div className="grid gap-5">
							<SheetHeader>
								<SheetTitle>{detailQuery.data.project.title}</SheetTitle>
							</SheetHeader>
							<Card className="border-slate-200 shadow-none">
								<CardHeader>
									<CardTitle>Payment detail</CardTitle>
								</CardHeader>
								<CardContent className="grid gap-4 text-sm">
									<div className="grid gap-2 md:grid-cols-2">
										<div>
											<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
												Transaction
											</p>
											<p className="mt-1 font-medium">
												{detailQuery.data.transaction.id}
											</p>
										</div>
										<div>
											<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
												Provider order
											</p>
											<p className="mt-1 font-medium">
												{detailQuery.data.transaction.providerOrderId ||
													"Unavailable"}
											</p>
										</div>
										<div>
											<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
												Amount
											</p>
											<p className="mt-1 font-medium">
												{formatRupees(detailQuery.data.transaction.amount)}
											</p>
										</div>
										<div>
											<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
												Status
											</p>
											<div className="mt-1 flex flex-wrap gap-1">
												<Badge
													variant={paymentTone(
														detailQuery.data.transaction.status,
													)}
												>
													{detailQuery.data.transaction.status}
												</Badge>
												{detailQuery.data.duplicateCharge ? (
													<DuplicateChargeBadge />
												) : null}
											</div>
										</div>
									</div>
									<div className="rounded-2xl bg-slate-50 p-4">
										<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
											Payout blockers
										</p>
										{detailQuery.data.payout.blockers.length ? (
											<ul className="mt-2 grid gap-2 text-sm text-slate-700">
												{detailQuery.data.payout.blockers.map((blocker) => (
													<li key={blocker}>{blocker}</li>
												))}
											</ul>
										) : (
											<p className="mt-2 text-sm text-emerald-700">
												Payout account is synced and ready.
											</p>
										)}
										{detailQuery.data.payout.staleStubAccount &&
										detailQuery.data.payout.accountId ? (
											<Button
												className="mt-3"
												variant="outline"
												size="sm"
												onClick={() => {
													const accountId = detailQuery.data?.payout.accountId;
													if (accountId) resyncMutation.mutate(accountId);
												}}
												isLoading={resyncMutation.isPending}
											>
												Re-sync payout account
											</Button>
										) : null}
									</div>
								</CardContent>
							</Card>

							{detailKind !== null && detailKind !== "milestone" ? (
								<Card className="border-slate-200 shadow-none">
									<CardHeader>
										<CardTitle>{MILESTONE_LESS_LABELS[detailKind]}</CardTitle>
										<CardDescription>
											{milestoneLessExplanation(
												detailKind,
												detailQuery.data.transaction.status,
											)}
										</CardDescription>
									</CardHeader>
								</Card>
							) : null}

							{/*
							 * `adminReleaseMilestonePayment` refuses a milestone whose
							 * funding status is `disputed`: its payment moves only by
							 * resolving the dispute, below.
							 */}
							{/*
							 * A duplicate charge is never released: the backend refuses
							 * it with 409 whatever the milestone's state, so it gets the
							 * refund path instead of any of the three release cards below.
							 */}
							{detailKind === "milestone" &&
							detailQuery.data.transaction.status === "held" &&
							detailQuery.data.duplicateCharge ? (
								<Card className="border-rose-200 shadow-none">
									<CardHeader>
										<CardTitle>Duplicate charge</CardTitle>
										<CardDescription>
											{duplicateChargeGuidance(
												detailDispute,
												permittedOutcomes.includes("full_refund"),
											)}
										</CardDescription>
									</CardHeader>
								</Card>
							) : null}

							{detailKind === "milestone" &&
							detailQuery.data.transaction.status === "held" &&
							!detailQuery.data.duplicateCharge &&
							detailQuery.data.milestone.fundingStatus === "disputed" ? (
								<Card className="border-slate-200 shadow-none">
									<CardHeader>
										<CardTitle>Admin release</CardTitle>
										<CardDescription>
											This milestone's payment is under dispute, so it is paid
											out or refunded only by resolving the dispute.
										</CardDescription>
									</CardHeader>
								</Card>
							) : null}

							{detailKind === "milestone" &&
							detailQuery.data.transaction.status === "held" &&
							!detailQuery.data.duplicateCharge &&
							detailQuery.data.milestone.fundingStatus !== "disputed" &&
							detailQuery.data.milestone.status === "completed" ? (
								<Card className="border-slate-200 shadow-none">
									<CardHeader>
										<CardTitle>Admin release</CardTitle>
										<CardDescription>
											Use only when milestone completion is clear and the normal
											homeowner release path is blocked.
										</CardDescription>
									</CardHeader>
									<CardContent className="grid gap-4">
										<Textarea
											rows={4}
											value={releaseReason}
											onChange={(event) => setReleaseReason(event.target.value)}
											placeholder="Explain why this release is being forced."
										/>
										<Button
											onClick={() =>
												releaseMutation.mutate({
													paymentId: detailQuery.data?.transaction.id,
													reason: releaseReason,
												})
											}
											isLoading={releaseMutation.isPending}
											disabled={releaseReason.trim().length < 10}
										>
											Release payment
										</Button>
									</CardContent>
								</Card>
							) : null}

							{/*
							 * The API refuses a release until the milestone is `completed`
							 * (`adminReleaseMilestonePayment`). Offering the button anyway is a
							 * button that can only 400, so the reason is shown instead.
							 */}
							{detailKind === "milestone" &&
							detailQuery.data.transaction.status === "held" &&
							!detailQuery.data.duplicateCharge &&
							detailQuery.data.milestone.fundingStatus !== "disputed" &&
							detailQuery.data.milestone.status !== "completed" ? (
								<Card className="border-slate-200 shadow-none">
									<CardHeader>
										<CardTitle>Admin release</CardTitle>
										<CardDescription>
											This milestone is not marked completed, so its payment
											cannot be released. The homeowner releases it once the
											work is completed; a disputed payment is paid by resolving
											its dispute.
										</CardDescription>
									</CardHeader>
								</Card>
							) : null}

							{detailKind === "milestone" ? (
								<Card className="border-slate-200 shadow-none">
									<CardHeader>
										<CardTitle>Dispute workflow</CardTitle>
										<CardDescription>
											Open a dispute to keep the case explicit, then resolve
											with release or refund.
										</CardDescription>
									</CardHeader>
									<CardContent className="grid gap-5">
										<div className="grid gap-4 rounded-2xl border p-4">
											<div className="flex items-center justify-between">
												<p className="font-medium">Current dispute</p>
												{detailQuery.data.latestDispute ? (
													<Badge
														variant={disputeTone(
															detailQuery.data.latestDispute.status,
														)}
													>
														{detailQuery.data.latestDispute.status}
													</Badge>
												) : (
													<Badge>No dispute</Badge>
												)}
											</div>
											{detailQuery.data.latestDispute ? (
												<p className="text-sm text-slate-700">
													{detailQuery.data.latestDispute.resolution ||
														detailQuery.data.latestDispute.details ||
														"No resolution note yet."}
												</p>
											) : (
												<p className="text-sm text-muted-foreground">
													No dispute has been recorded for this payment yet.
												</p>
											)}
										</div>

										{detailDispute?.status === "resolving" ? (
											<p className="text-sm text-muted-foreground">
												This dispute cannot be edited while its resolution is in
												progress.
											</p>
										) : (
											<div className="grid gap-3">
												<p className="text-sm font-medium">
													Create or update dispute
												</p>
												<Select
													value={disputeReason}
													onValueChange={(value) =>
														setDisputeReason(
															value as AdminPaymentDispute["reason"],
														)
													}
												>
													<SelectTrigger>
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															<SelectItem value="incomplete_work">
																Incomplete work
															</SelectItem>
															<SelectItem value="poor_quality">
																Poor quality
															</SelectItem>
															<SelectItem value="wrong_materials">
																Wrong materials
															</SelectItem>
															<SelectItem value="other">Other</SelectItem>
														</SelectGroup>
													</SelectContent>
												</Select>
												<Textarea
													rows={4}
													value={disputeDetails}
													onChange={(event) =>
														setDisputeDetails(event.target.value)
													}
													placeholder="Summarize evidence, calls, and the current blocking issue."
												/>
												<Button
													variant="outline"
													onClick={() =>
														disputeMutation.mutate({
															paymentId: detailQuery.data?.transaction.id,
															reason: disputeReason,
															details: disputeDetails,
														})
													}
													isLoading={disputeMutation.isPending}
													disabled={disputeDetails.trim().length < 10}
												>
													Save dispute
												</Button>
											</div>
										)}

										{detailDispute &&
										(detailDispute.status === "open" ||
											detailDispute.status === "under_review") ? (
											resolvableOutcomes.length > 0 ? (
												<ResolveDisputePanel
													key={detailDispute.id}
													payment={detailQuery.data}
													dispute={detailDispute}
													outcomes={resolvableOutcomes}
													onSettled={invalidatePayments}
												/>
											) : (
												<p className="text-sm text-muted-foreground">
													{detailQuery.data.duplicateCharge
														? "Your role cannot issue the full refund this duplicate charge needs."
														: "Your role cannot resolve disputes."}
												</p>
											)
										) : null}

										{detailDispute?.status === "resolving" ? (
											<ResolutionProgress
												key={detailDispute.id}
												dispute={detailDispute}
												outcomes={permittedOutcomes}
												onSettled={invalidatePayments}
											/>
										) : null}
									</CardContent>
								</Card>
							) : null}
						</div>
					) : null}
				</SheetContent>
			</Sheet>
			<ReconcileConfirmDialog
				open={reconcileConfirm !== null}
				title={
					reconcileConfirm === "payouts"
						? "Reconcile pending payouts?"
						: "Reconcile pending refunds?"
				}
				body={
					reconcileConfirm === "payouts"
						? "Re-check the attempted payouts against the payment provider? This only records a release the provider confirms."
						: `Re-check ${pendingRefunds} refund(s) against the payment provider? This will only advance state when the provider confirms terminal.`
				}
				confirmLabel="Reconcile"
				onConfirm={() => {
					const run = reconcileConfirm;
					setReconcileConfirm(null);
					if (run === "payouts") reconcilePayoutsMutation.mutate();
					else reconcileMutation.mutate();
				}}
				onCancel={() => setReconcileConfirm(null)}
			/>
		</div>
	);
}
